"use client";

// The database, in the FIRST render — server and client alike.
//
// Before this existed, SSR rendered the static seed in lib/ironside-script-data
// while the real stages, comments and drafts lived only in sqlite, and the
// browser learned about them from <StoreSync>'s poll. Two bugs came out of that
// one gap:
//
//   1. A hydration mismatch, thrown reliably by the editor. React 18 hydrates
//      Suspense boundaries SELECTIVELY, so StoreSync's effect fires and its
//      fetch resolves (localhost: single-digit ms) before the editor's boundary
//      hydrates. That subtree then hydrated against database state while the
//      server's HTML still said seed — "Scripts not yet submitted" against
//      "Edits have been requested" — and React threw the boundary away and
//      re-rendered it on the client.
//   2. The same staleness, visible: every first paint showed seed data and then
//      popped to the truth a moment later.
//
// Both are the same fix: give the server the rows, and hand the browser the very
// same rows in the HTML, so the hydration render already agrees.
//
// APPLIED DURING RENDER, NOT IN AN EFFECT, AND THAT IS THE ENTIRE POINT. An
// effect runs after the children have hydrated, which is exactly the ordering
// that caused the mismatch. A parent's render body runs before its children
// render, so mutating here — impure as it looks — is what makes the stores warm
// before anything reads them. It is also why children are nested INSIDE this
// component instead of it sitting beside them as a sibling: nesting is what
// guarantees the ordering, and a sibling would only get it by luck.

// ─── ONLY ON A DOCUMENT LOAD, AND WITH ITS CURSOR ───────────────────────────
//
// The layout renders this on every router.refresh() and every revalidating
// server action too, and each one used to carry the whole dump (1.8 MB on the
// dev db) for a browser that had already seeded and dropped it. Now the layout
// hands rows only to a document load (lib/server/store-seed.ts), with
// `cursor`, the epoch and seq the rows were read at, from the same
// transaction. Everything else gets `cursor: null`
// and `rows: []`, which is NO SEED: nothing is applied, on either side.
//
// The cursor is what makes StoreSync's mount poll a 304: the tab starts at the
// seed's seq instead of asking for the whole store a second time.

// ─── AND ONLY THE GROUPS ITS ROUTE READS ────────────────────────────────────
//
// `groups` names what `rows` is the whole of (lib/server/store-seed.ts). The
// server's replace drops keys of those groups only, so a /brands render — no
// groups — leaves the process's script maps as the last scripts render left
// them, and the browser holds exactly those groups: a page reading another one
// waits for it (components/store-group-gate.tsx) while StoreSync loads it.

import { applyAllStores, type StoreRow } from "@/lib/store-hydrate";
import { setStoreCursor, storeCursor, type StoreCursor } from "@/lib/store-cursor";
import type { StoreGroup } from "@/lib/store-groups";

/** Client-side, the seed happens once — every later answer arrives via StoreSync's
 *  poll, which knows how to merge around a user's own in-flight writes. "Once"
 *  is "while the tab has no cursor": the seed sets one, and so does StoreSync's
 *  first full poll, after which a seed would only roll the tab back to an older
 *  read. On the server there is no "once": each document load re-applies the
 *  rows it just read, and the module's maps are a per-process cache of the
 *  database.
 *
 *  ⚠️ AND ON THE SERVER THE SEED REPLACES, because rows ARE deleted (Delete
 *  week's dates, a stage retracted). Merged, a key deleted since the process's last
 *  render stayed in its maps for ever: the next reload server-rendered the
 *  deleted row, and the browser — seeded from rows without it — hydrated
 *  against a page that disagreed. Only a REAL
 *  seed replaces, one with a cursor: a signed-out render, a refresh and an
 *  action all pass `[]` with no cursor, which means "no seed", not "the store
 *  is empty", and replacing with it would empty the stores.
 *
 *  ⚠️ NO GRADES, ON EITHER SIDE. The seed is a dump, and
 *  no dump carries `grade` (lib/store-groups.ts), so the server's maps never
 *  hold one and the server renders none: each editor row draws its
 *  grade-loading state in the HTML and in the hydration render alike, then
 *  asks for its grade (loadGradeRecord). Both sides apply the seed as a dump of
 *  its groups, so neither believes it is current on a grade it was never sent.
 *
 *  ⚠️ A BROWSER WHOSE FIRST SEED IS "NO SEED" waits for StoreSync. That is the
 *  sign-in landing: the sign-in is a server action whose redirect draws the
 *  signed-in shell into its RSC answer, so the shell's first render on the
 *  client is cursorless. The stores stay cold and hold no group (the page
 *  shows the group gate's loading state, not "no such script") until the
 *  mount poll, which has no cursor to send, brings the page's groups whole. */
export function StoreSeed({
  rows, cursor, groups, children,
}: { rows: StoreRow[]; cursor: StoreCursor | null; groups: StoreGroup[]; children: React.ReactNode }) {
  if (cursor !== null) {
    if (typeof window === "undefined") applyAllStores(rows, [], groups, groups);
    else if (storeCursor() === null) {
      // During render on purpose: the stores must be warm before children read them (see the header).
      setStoreCursor(cursor);
      applyAllStores(rows, [], false, groups);
    }
  }
  return <>{children}</>;
}
